import { forwardRef, useCallback, useEffect, useState } from "react";

import {
  XCircleFill,
  PlusCircle,
} from "@hl/base-components/lib/assets/icons.generated";
import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import {
  OUTLINE_COLOR,
  SECONDARY_COLOR,
} from "@hl/base-components/lib/theme/button";
import { BACKGROUND_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { useModalStack } from "@hl/shared-features/lib/features/modal";
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Grid,
  Group,
  Loader,
  SelectItemProps,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";

import useEnableCategoriesAndTagsInput from "~/features/collections/forms/CreateCollectionMultistepForm/tags-categories/useEnableCategoriesAndTagsInput";
import useTags from "~/features/collections/forms/CreateCollectionMultistepForm/tags-categories/useTags";

type Props = {
  userTags?: TagInputType[] | null;
  onChangeTags: (tags: TagInputType[]) => void;
  chainId: string;
};

export type TagInputType = {
  name: string;
  slug: string;
};

const TagsInput = ({ userTags, onChangeTags, chainId }: Props) => {
  const { pushModal, popModal } = useModalStack();
  const enableTags = useEnableCategoriesAndTagsInput(chainId);

  useEffect(() => {
    if (!enableTags) {
      onChangeTags([]);
    }
  }, [enableTags]);

  if (!enableTags) return <></>;

  const handleAddTag = () =>
    pushModal(
      <AddTags
        onChangeTags={onChangeTags}
        userTags={userTags}
        onClose={popModal}
      />,
      {
        size: "lg",
        title: (
          <Text size="md" fw={WEIGHT_BOLD}>
            Add tags
          </Text>
        ),
      }
    );

  return (
    <Group>
      <Text component="label">Tags</Text>
      <Text size="sm">
        Add your own optional tags to let people know more about the work. These
        will also appear on your collection page.
      </Text>
      {userTags?.map((tag) => {
        return (
          <Button key={tag?.slug} color={SECONDARY_COLOR}>
            # {tag?.name}
          </Button>
        );
      })}
      <Button
        leftIcon={<PlusCircle />}
        color={OUTLINE_COLOR}
        onClick={handleAddTag}
      >
        Add tags
      </Button>
    </Group>
  );
};

const AddTags = ({
  userTags,
  onChangeTags,
  onClose,
}: {
  userTags?: TagInputType[] | null;
  onChangeTags: (tags: TagInputType[]) => void;
  onClose: () => void;
}) => {
  const [searchString, setSearchString] = useState<string>("");
  const [debouncedSearchString] = useDebouncedValue(searchString, 500);
  const [updatedTags, setUpdatedTags] = useState<TagInputType[]>(
    userTags || []
  );
  const [isFocused, setIsFocused] = useState(false);
  const { tagsSearched, getSlug, loading } = useTags(
    debouncedSearchString,
    isFocused
  );

  const handleSave = useCallback(() => {
    onChangeTags(updatedTags);
    onClose();
  }, [updatedTags, onClose]);

  const onRemoveTag = useCallback(
    (slug: string) =>
      setUpdatedTags(updatedTags?.filter((tag) => tag.slug !== slug) || []),
    [updatedTags]
  );

  const onAddCategory = useCallback(
    (tagToAdd: TagInputType) => {
      const tagExists = updatedTags.find((tag) => tagToAdd.slug === tag.slug);
      if (!tagExists) {
        setUpdatedTags([...(updatedTags ? updatedTags : []), tagToAdd]);
      }
    },
    [updatedTags]
  );

  const onCreateAndAddTag = (name: string) => {
    const formattedName = name.trim();
    if (!formattedName) return;

    const tagExists = data.find((item) => item.label === formattedName);
    if (tagExists) {
      return onAddCategory({
        name: tagExists.label,
        slug: tagExists.value,
      });
    }

    onAddCategory({
      slug: getSlug(formattedName),
      name: formattedName,
    });
  };

  interface ItemProps extends SelectItemProps {
    count: string;
  }

  const AutoCompleteItem = forwardRef<HTMLDivElement, ItemProps>(
    ({ label, count, ...rest }: ItemProps, ref) => (
      <Group ref={ref} {...rest} spacing={6} position="apart" w="100%">
        <Text size="sm">{label}</Text>
        <Badge color={BACKGROUND_COLOR.QUATERNARY} size="xs">
          <Text size="xs" fw={WEIGHT_BOLD}>
            {count}
          </Text>
        </Badge>
      </Group>
    )
  );

  const data = tagsSearched
    .map((item) => ({
      label: item.name,
      value: item.slug,
      count: item.count,
    }))
    .sort((a, b) => b.count - a.count);
  const disableAdd = updatedTags.length >= 10;

  return (
    <Stack spacing={0}>
      <Grid justify="space-between" align="center" gutter={8} mb="sm">
        <Grid.Col span="auto">
          <Autocomplete
            maxLength={20}
            icon={loading ? <Loader /> : undefined}
            onFocus={() => setIsFocused(true)}
            disabled={disableAdd}
            styles={{
              input: {
                margin: 0,
              },
            }}
            placeholder="Tag name"
            itemComponent={AutoCompleteItem}
            data={data}
            onChange={(value) => {
              setSearchString(value.toLowerCase());
            }}
            onItemSubmit={(item) => {
              onAddCategory({ slug: item.value, name: item.label });
              setSearchString("");
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                onCreateAndAddTag(searchString);
              }
            }}
            value={searchString}
          />
        </Grid.Col>
        <Grid.Col span="content">
          <Button
            disabled={disableAdd}
            h={48}
            size="lg"
            onClick={() => onCreateAndAddTag(searchString)}
            color={SECONDARY_COLOR}
            rightIcon={<PlusCircle />}
          >
            Add tag
          </Button>
        </Grid.Col>
      </Grid>
      <Box mt="xs" mih={200}>
        <SimpleGrid cols={3}>
          {updatedTags?.map((tag) => {
            return (
              <Button
                key={tag.slug}
                onClick={() => onRemoveTag(tag.slug)}
                color={SECONDARY_COLOR}
                rightIcon={<XCircleFill />}
              >
                # {tag.name}
              </Button>
            );
          })}
        </SimpleGrid>
      </Box>
      <ModalSection>
        <Button size="xl" onClick={handleSave} fullWidth>
          Save
        </Button>
      </ModalSection>
    </Stack>
  );
};

export default TagsInput;
